import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AmendmentLauncher } from '@app/features/amendments/amendment-launcher.service';
import { ContractService } from '@app/features/contracts/contract-service';
import { Employee } from '@app/features/employees/employee.model';
import { SearchEmployee } from '@app/features/employees/search-employee/search-employee';
import { LayoutService } from '@app/nav/layout/layout.service';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import {
  LucideCalendarPlus,
  LucideCalendarSync,
  LucideFilePenLine,
  LucidePlus,
} from '@lucide/angular';
import { Contract, ContractListItem } from '../../contract.model';
import { ContractsTable } from '../../contracts-table/contracts-table';
import { SearchContractForm } from '../../search-contract-form/search-contract-form';
import { ContractLayout } from '../contract-layout/contract-layout';

@Component({
  selector: 'app-contracts-dashboard',
  imports: [
    CommonModule,
    MatButtonModule,
    ContractsTable,
    SearchEmployee,
    ReactiveFormsModule,
    LucidePlus,
    LucideCalendarSync,
    LucideCalendarPlus,
    LucideFilePenLine,
    MatMenuModule,
    MatCardModule,
    MatIconModule,
    SearchContractForm,
    ContractLayout,
  ],
  templateUrl: './contracts-dashboard.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./contracts-dashboard.scss'],
})
export class ContractsDashboard extends RxjsDirective implements OnInit {
  readonly dialog = inject(MatDialog);
  readonly cdr = inject(ChangeDetectorRef);
  readonly contractService = inject(ContractService);
  readonly location = inject(Location);
  readonly layoutService = inject(LayoutService);
  private readonly amendmentLauncher = inject(AmendmentLauncher);

  @ViewChild(ContractsTable, { static: true }) contractsTable!: ContractsTable;

  employeeControl = new FormControl<Employee | null>(null);
  contract: Contract | undefined;

  /** @param renewal une prolongation sans interruption, qui part d'un contrat précédent */
  openNewEmbaucheDialog(renewal: boolean): void {
    this.layoutService.setContract({
      employee: this.employeeControl.value,
      renewal,
    } as Contract);
  }

  /** L'avenant du contrat en cours du salarié, sans avoir à retrouver ce contrat. */
  establishAmendment(): void {
    const employeeId = this.employeeControl.value?.id;
    if (employeeId) {
      this.register(this.amendmentLauncher.establishForEmployee(employeeId).subscribe());
    }
  }

  ngOnInit(): void {
    this.register(
      this.layoutService.contract$.subscribe((contract) => {
        this.contract = contract;
        if (contract && contract.id) {
          this.location.replaceState(`/contrats/${contract.id}`);
        } else {
          this.location.replaceState(`/contrats`);
        }
      }),
      this.employeeControl.valueChanges.subscribe(() => {
        this.cdr.markForCheck();
      }),
    );
  }

  onEditContract($event: ContractListItem) {
    this.contractService.findOne($event.id).subscribe((contract) => {
      this.layoutService.setContract(contract);
      this.layoutService.setLayout('panel-left');
      this.cdr.markForCheck();
    });
  }

  onEditSocialSecurity($event: ContractListItem) {
    this.register(
      this.contractService.findOne($event.id).subscribe((contract) => {
        this.layoutService.setSocialSecurity(contract);
        this.cdr.markForCheck();
      }),
    );
  }
}
